import React, { useEffect, useState } from "react";

// The ceremony-reveal UI (stage-by-stage semifinalist/winner reveal) is kept
// exactly as designed — that presentation logic is sound and doesn't need
// game data to exist. What's changed: it now reads from a real API response
// via fetchWinners(), and shows an honest "not ready yet" state instead of
// silently displaying hardcoded fake names when there's no game data —
// which, until Phase 2 ships, is always, since nothing produces scores yet.

const ReunionWinners = ({ event, fetchWinners, finalizeResults }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [stage, setStage] = useState(0);

  const loadWinners = async () => {
    setLoading(true);
    const result = await fetchWinners();
    setData(result);
    setLoading(false);
  };

  useEffect(() => {
    loadWinners();
    // eslint-disable-next-line
  }, []);

  const handleFinalize = async () => {
    setFinalizing(true);
    await finalizeResults();
    await loadWinners();
    setFinalizing(false);
  };

  if (loading) {
    return (
      <div className="rounded-3xl bg-white/5 border border-white/10 p-12 text-center text-emerald-300">
        Loading results...
      </div>
    );
  }

  if (!data || !data.resultsReady) {
    return (
      <div className="rounded-3xl bg-white/5 border border-white/10 p-12 text-center">
        <div className="text-6xl mb-5">⏳</div>
        <h2 className="text-3xl font-black mb-3">Results Not Ready Yet</h2>
        <p className="text-emerald-200 max-w-md mx-auto mb-6">
          Winner announcement unlocks once participants have played and been
          scored. Nobody has a recorded score for this Reunion yet.
        </p>
        {!event.isLive && (
          <button
            onClick={handleFinalize}
            disabled={finalizing}
            className="px-8 py-3 rounded-2xl bg-gradient-to-r from-violet-500 to-purple-600 font-bold disabled:opacity-40"
          >
            {finalizing ? "Computing..." : "🏁 Compute Semifinalists & Winners"}
          </button>
        )}
      </div>
    );
  }

  const { semifinalists, winners } = data;
  const next = () => setStage((s) => s + 1);

  return (
    <div className="space-y-2">
      {stage === 0 && (
        <div className="rounded-3xl bg-white/5 border border-white/10 p-12 text-center">
          <div className="text-7xl mb-5">🎊</div>
          <h1 className="text-4xl lg:text-6xl font-black mb-5">Reunion Awards</h1>
          <p className="text-emerald-200 mb-8">{event.hallName}</p>
          <button
            onClick={next}
            className="px-8 py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-green-600 font-bold text-lg"
          >
            ▶ Begin Ceremony
          </button>
        </div>
      )}

      {stage >= 1 && stage <= semifinalists.length && (
        <div className="rounded-3xl bg-gradient-to-br from-yellow-500/10 via-white/5 to-orange-500/10 border border-yellow-500/30 p-12 text-center">
          <div className="text-7xl animate-pulse mb-6">⭐</div>
          <div className="uppercase tracking-[0.3em] text-yellow-300 mb-4">
            Semifinalist
          </div>
          <h2 className="text-4xl lg:text-6xl font-black mb-4 text-cyan-200">
            {semifinalists[stage - 1].name}
          </h2>
          <div className="text-lg text-emerald-200">
            ⭐ {semifinalists[stage - 1].score} Marks
          </div>
          <div className="text-lg text-emerald-300 mb-8">
            ⏱ {semifinalists[stage - 1].completionTimeSeconds}s
          </div>
          <div className="text-5xl mb-8">👏👏👏👏👏</div>
          <button
            onClick={next}
            className="px-8 py-4 rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-500 font-bold"
          >
            {stage === semifinalists.length
              ? "👑 Reveal Winners"
              : "➡ Next Semifinalist"}
          </button>
        </div>
      )}

      {stage === semifinalists.length + 1 && (
        <div className="rounded-3xl bg-white/5 border border-white/10 p-10">
          <h2 className="text-center text-4xl font-black mb-3">
            🌟 Semifinalists
          </h2>
          <div className="space-y-2 mb-3">
            {semifinalists.map((p) => (
              <div
                key={p._id}
                className="rounded-2xl bg-white/5 border border-white/10 p-4 flex justify-between"
              >
                <div className="text-white">⭐ {p.name}</div>
                <div>
                  {p.score} • {p.completionTimeSeconds}s
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <button
              onClick={next}
              className="px-10 py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-600 font-bold"
            >
              🥉 Start Winner Reveal
            </button>
          </div>
        </div>
      )}

      {stage >= semifinalists.length + 2 &&
        stage <= semifinalists.length + 1 + winners.length && (
          <WinnerRevealCard
            winners={winners}
            stage={stage}
            baseStage={semifinalists.length + 2}
            event={event}
            onNext={next}
          />
        )}
    </div>
  );
};

const WinnerRevealCard = ({ winners, stage, baseStage, event, onNext }) => {
  const idx = stage - baseStage; // reveal order: 0 = first revealed (lowest rank)
  const isLast = idx === winners.length - 1;

  // winners[] arrives sorted best-first (winners[0] = champion). Reveal order
  // is suspense-first: lowest rank shown first, champion last — i.e. reversed.
  const displayIdx = winners.length - 1 - idx;
  const displayWinner = winners[displayIdx];

  // Titles/medals are indexed by REVEAL order (idx), not sorted order —
  // idx 0 is always "Third Place" regardless of how many winners there are.
  const titles = ["Third Place", "Second Place", "Champion"];
  const medals = ["🥉", "🥈", "🥇"];
  const titleIdx = titles.length - winners.length + idx; // aligns "Champion" to the last reveal

  return (
    <div className="rounded-3xl bg-gradient-to-br from-yellow-500/15 via-white/5 to-orange-500/15 border border-yellow-400/30 p-12 text-center">
      <div className="text-8xl mb-6">{medals[titleIdx] || "🏅"}</div>
      <div className="uppercase tracking-[0.3em] text-yellow-300 mb-4">
        {titles[titleIdx] || `Rank ${displayIdx + 1}`}
      </div>
      <h2 className="text-5xl lg:text-7xl font-black mb-5 text-cyan-200">
        {displayWinner.name}
      </h2>
      <div className="text-xl">⭐ {displayWinner.score} Marks</div>
      <div className="text-lg text-emerald-300 mb-8">
        ⏱ {displayWinner.completionTimeSeconds}s
      </div>
      <div className="text-5xl mb-8">🎉🎊🏆✨</div>

      {!isLast ? (
        <button
          onClick={onNext}
          className="px-10 py-4 rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-500 font-bold"
        >
          Reveal Next
        </button>
      ) : (
        <div className="space-y-3">
          <div className="text-4xl font-black">🎊 Congratulations 🎊</div>
          <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/20 p-6">
            <div className="text-2xl font-black">{event.prizeDetails}</div>
            <div className="mt-3 text-emerald-200">
              Thank you to every participant for making this Reunion
              memorable.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReunionWinners;
